import { getRegionById } from '@/shared/utils/coloradoRegions';

/**
 * Assemble the Advanced Query form state into a single structured query
 * payload. This payload is the contract handed to the follow-up task that
 * starts an Expert Query Bili session; keeping assembly in a pure function
 * makes it unit-testable and keeps the page component focused on rendering.
 *
 * Search terms are free text (not column filters): the engine samples each
 * source at runtime and has no fixed per-source schema, so the user supplies
 * words/phrases to look for and Bili applies them per source. Terms are joined
 * with AND / OR / NOT logic.
 *
 * @param {Object} input
 * @param {Object[]} input.sources - Selected normalized source objects.
 * @param {Object[]} input.terms - Rows of { text, operator }.
 * @param {Object} input.timeRange - { option: 'latest' | 'custom', start, end }.
 * @param {string} input.regionId - Selected Colorado region id.
 * @returns {Object} Structured payload: { sources, terms, timeRange, area }.
 */
export function buildQueryPayload({
    sources = [],
    terms = [],
    timeRange = { option: 'latest', start: '', end: '' },
    regionId = 'statewide',
}) {
    const region = getRegionById(regionId);

    // Keep only rows with a non-empty term. The first surviving term carries
    // no joining operator.
    const searchTerms = terms
        .filter((t) => t.text && t.text.trim())
        .map((t, index) => ({
            term: t.text.trim(),
            operator: index === 0 ? null : t.operator || 'and',
        }));

    return {
        sources: sources.map((s) => ({ id: s.id, name: s.name })),
        terms: searchTerms,
        timeRange:
            timeRange.option === 'custom'
                ? {
                      option: 'custom',
                      start: timeRange.start || null,
                      end: timeRange.end || null,
                  }
                : { option: timeRange.option || 'latest' },
        area: region
            ? { regionId: region.id, name: region.name, bbox: region.bbox }
            : { regionId, name: null, bbox: null },
    };
}

/**
 * Human-readable, one-line description of an assembled payload. Used for the
 * on-page confirmation now, and available as the seed message when the
 * follow-up task hands the query to Bili.
 *
 * @param {Object} payload - Output of buildQueryPayload.
 * @returns {string}
 */
export function describeQueryPayload(payload) {
    const sourceNames =
        payload.sources.map((s) => s.name).join(', ') || 'no data sources';

    const terms =
        payload.terms.length > 0
            ? payload.terms
                  .map((t) => {
                      const prefix = t.operator
                          ? `${t.operator.toUpperCase()} `
                          : '';
                      return `${prefix}"${t.term}"`;
                  })
                  .join(' ')
            : 'no search terms';

    const time =
        payload.timeRange.option === 'custom'
            ? `${payload.timeRange.start || '?'} to ${payload.timeRange.end || '?'}`
            : payload.timeRange.option;

    const area = payload.area.name || 'unspecified area';

    return `Query ${sourceNames} for ${terms}; time range: ${time}; area: ${area}.`;
}
